import { useEffect, useState } from 'react'
import Head from 'next/head'
import Script from 'next/script'
import Link from 'next/link'
import Image from 'next/image'
import { motion } from 'framer-motion'
import { s3Static } from '../../../utils/constants'
import { contentDate } from '../../../utils/article-date-time'
import { BlogsList } from '../../../components/blogs/BlogList'
import { blogsMaster } from '../../../utils/sg-blogs-data'
import { getRequestJSON } from '../../../api/index/location-api'
import { useRouter } from 'next/router'
import { getWpNewPostsByCategory, getWpPostsByCategory } from '../../../lib/service'
import { getWpHealthFitness, getWpNewHealthFitness, setWpHealthFitness, setWpNewHealthFitness } from '../../../service/wp-blogs-store'
import { PersonalTrainerHead } from '@/index/service-category/personal-trainer/PersonalTrainerHead'
import { PersonalTrainerBooking } from '@/index/service-category/personal-trainer/PersonalTrainerBooking'
import { openModal } from 'service/modal-service'
import { TrainerCustomerReviews } from '@/index/service-category/personal-trainer/TrainerCustomerReviews'
import { ptSingaporeFaqs } from 'utils/faq'
import { RequestProvider } from 'context/RequestContext'
import { PersonalTrainerWhyUs } from '@/index/service-category/personal-trainer/PersonalTrainerWhyUs'
import { getTestimonials } from 'api/user/profile-api'
import { DarkFaq } from '@/index/service-category/personal-trainer/DarkFaq'
import { localBusinessNode, buildServiceNode } from 'utils/local-business-schema'

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-10-25'

export async function getStaticProps() {
    const countryName = "Singapore"
    const { modifiedDate, month, year } = contentDate(CONTENT_REVIEWED);
    const fitnessBlogs = blogsMaster['health-fitness'].blogArray;
    const wpPosts = getWpHealthFitness().length > 0 ?  getWpHealthFitness() : await getWpPostsByCategory(6, "health-fitness");
    setWpHealthFitness(wpPosts)
    const wpNewPosts = getWpNewHealthFitness().length > 0 ?  getWpNewHealthFitness() : await getWpNewPostsByCategory(6, "health-fitness");
    setWpNewHealthFitness(wpNewPosts)

    const allRequestCategorySettings = await getRequestJSON("Singapore")
    const testimonials = await getTestimonials("Fitness Trainer", 0, 6)
    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Fitness Trainer")

  return {
      props: {
        requestCategorySettings,
        modifiedDate,
        month,
        year,
        fitnessBlogs,
        wpPosts,
        wpNewPosts,
        testimonials
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 10 seconds
      revalidate: 259200, // In seconds
  }
}

export default function Home(props) {
    const router = useRouter()
    const modifiedDate = props.modifiedDate
    const month = props.month
    const year = props.year
    const fitnessBlogs = props.fitnessBlogs
    const wpPosts = props.wpPosts
    const wpNewPosts = props.wpNewPosts
    const requestCategorySettings = props.requestCategorySettings;
    const testimonials = props.testimonials

    const [ requestForm, setRequestForm ] = useState(false)

    const requestType = "Fitness Trainer"
    const [isQuickBook, setIsQuickBook] = useState(false)
    const [isCustomBook, setIsCustomBook] = useState(false)

    const openRequestForm = () => {
        window.scrollTo({ top: 0 });
        openModal(setRequestForm)
    }

    useEffect(() => {
        if(router.query.requestForm === "true"){
            setRequestForm(true)
            openModal(setRequestForm)
        }
    }, [router])

  return (
    <>
      <Head>
        <title>Personal Trainer for Functional Fitness in Singapore</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content="Enhance your daily performance with functional fitness training in Singapore. Work with certified trainers for better mobility, core strength, and balance through practical movement-based workouts."/>
        <meta name="keywords" content="functional fitness trainer, functional training Singapore, mobility training, core strength, balance exercises, movement-based workouts, certified functional trainer"/>
        <meta name="robots" content="index, follow" />
        <meta name="author" content="StringsSG - Personal Training"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"   content="https://www.stringssg.com/sg/personal-trainer/personal-trainer-for-functional-fitness" />
        <meta property="og:type"   content="article" />
        <meta property="og:title"    content={`Personal Trainer for Functional Fitness in Singapore`}/>
        <meta property="og:description" content="Enhance your daily performance with functional fitness training in Singapore. Work with certified trainers for better mobility, core strength, and balance through practical movement-based workouts." />
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:published_time" content="2025-01-26T07:30:46+00:00" />
        <meta property="article:modified_time" content={`${modifiedDate}`} />
        <meta property="og:image"  content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/health+and+fitness/Functional-training.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/health+and+fitness/Functional-training.webp" />
        <meta name="twitter:site" content="@stringssg"/>
        <meta name="twitter:url" content="https://www.stringssg.com/sg/personal-trainer/personal-trainer-for-functional-fitness"/>
        <meta name="twitter:title" content={`Personal Trainer for Functional Fitness in Singapore`}/>
        <meta name="twitter:description" content="Enhance your daily performance with functional fitness training in Singapore. Work with certified trainers for better mobility, core strength, and balance through practical movement-based workouts."/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href="https://www.stringssg.com/sg/personal-trainer/personal-trainer-for-functional-fitness" />
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html:
            `{"@context":"https://schema.org","@graph":[{"@context":"https://schema.org/","@type":"BlogPosting","mainEntityOfPage":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/personal-trainer/personal-trainer-for-functional-fitness"},"headline":"Personal Trainer for Functional Fitness in Singapore","description":"Enhance your daily performance with functional fitness training in Singapore. Work with certified trainers for better mobility, core strength, and balance through practical movement-based workouts.","image":["https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/health+and+fitness/Functional-training.webp"],"author":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/"},"publisher":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/","logo":{"@type":"ImageObject","url":"https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/logo.png"}},"datePublished":"2025-01-26T07:30:46+00:00","dateModified":"${modifiedDate}"},{"@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"personal-trainer","item":"https://www.stringssg.com/sg/personal-trainer"},{"@type":"ListItem","position":2,"name":"functional-fitness"}]},${JSON.stringify(localBusinessNode)},${JSON.stringify(buildServiceNode({ pageUrl: "https://www.stringssg.com/sg/personal-trainer/personal-trainer-for-functional-fitness", name: "Functional Fitness Personal Trainer Singapore", serviceType: "Personal Training", priceRange: "S$84.00 - S$110.00", image: "https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/health+and+fitness/lady-personal-trainer-helping-man.jpg" }))}]}`
          }}
        />
        </Head>
        <div>
            <RequestProvider>
                <PersonalTrainerHead
                    heroImage={s3Static + "blog/health+and+fitness/Functional-training.webp"}
                    title="Personal Trainer for Functional Fitness in Singapore"
                    heading="Personal Trainer for Functional Fitness in Singapore"
                    heroCopy="Functional strength and movement coaching from $84/session - built for everyday activity, IPPT prep and injury resilience."
                    setIsQuickBook={setIsQuickBook}
                    openRequestForm={openRequestForm}
                    requestForm={requestForm}
                    setRequestForm={setRequestForm}
                    isQuickBook={isQuickBook}
                    isCustomBook={isCustomBook}
                    requestType={requestType}
                    requestCategorySettings={requestCategorySettings}
                />
                <section className="max-w-5xl mx-auto py-10 px-4">
                    <div className="grid md:grid-cols-3 gap-10 items-center">
                        <header className='space-y-6 md:col-span-2'>
                            {/* <h2 className='text-2xl md:text-4xl text-center uppercase'>
                                <strong>Personal Trainer for Functional Fitness in Singapore</strong>
                            </h2> */}
                            <p className="text-gray-700 leading-7">
                                Improve your everyday performance and quality of life with a <strong>functional fitness trainer in Singapore</strong>. Build core strength, enhance mobility, and boost balance through movement-based workouts that mimic real-life activities, helping you feel stronger and more capable in daily tasks. With hundreds of successful clients across Singapore and our in-house certified trainers, we&apos;re ready to support your functional fitness journey.
                            </p>
                        </header>
                        <div className="relative w-full h-80 hidden md:block md:col-span-1">
                            <Image
                                src={s3Static + "personal-trainer/personal-trainer-png.png"}
                                fill
                                style={{ objectFit: "contain", objectPosition: "center" }}
                                placeholder="blur"
                                blurDataURL={s3Static + "placeholder.png"}
                                alt="Personal Trainer for Functional Fitness"
                                sizes="(max-width: 768px) 100vw, 33vw"
                            />
                        </div>
                    </div>

                    {/* Benefit Cards */}
                    <div className="grid md:grid-cols-3 gap-6 mt-10">
                        {[
                        {
                            icon: "🏃‍♂️",
                            title: "Enhanced Mobility",
                            desc: "Improve flexibility, range of motion, and joint health through dynamic stretches and functional movements.",
                        },
                        {
                            icon: "🥗",
                            title: "Core Strength Development",
                            desc: "Build a strong foundation with targeted exercises for better posture, stability, and injury prevention.",
                        },
                        {
                            icon: "⚖️",
                            title: "Balance & Coordination",
                            desc: "Improve proprioception and stability with exercises like single-leg stands and agility drills.",
                        },
                        {
                            icon: "🚀",
                            title: "Holistic Fitness",
                            desc: "Integrate strength, cardio, and mobility for complete physical fitness, not isolated muscle groups.",
                        },
                        {
                            icon: "🏠",
                            title: "Practical Home Training",
                            desc: "Learn workouts you can do anywhere with minimal equipment, making fitness sustainable.",
                        },
                        {
                            icon: "🔬",
                            title: "Certified Movement Experts",
                            desc: "Work with trainers certified in functional movement, yoga, or corrective exercise specialists.",
                        },
                        ].map((item, i) => (
                        <div
                            key={i}
                            className="bg-white rounded-3xl shadow-md p-6 text-center hover:shadow-xl transition transform hover:-translate-y-1"
                        >
                            <div className="text-5xl mb-4">{item.icon}</div>
                            <h3 className="font-semibold text-lg text-gray-800 mb-2">{item.title}</h3>
                            <p className="text-gray-600 leading-relaxed text-sm">{item.desc}</p>
                        </div>
                        ))}
                    </div>

                    {/* FAQ / Expectations */}
                    <div className="mt-14 space-y-6">
                        <h2 className="text-2xl font-bold text-gray-900">
                        How does functional fitness training help me?
                        </h2>
                        <p className="text-gray-700 leading-7">
                        Functional fitness focuses on movements that translate to real-life activities, making you stronger for daily tasks. Unlike traditional gym workouts, it emphasizes balance, coordination, and compound movements that improve overall quality of life and reduce injury risk.
                        </p>
                        <p className="text-sm text-gray-500 italic">
                        Tip: Functional training benefits athletes, seniors, and office workers alike by enhancing practical strength.
                        </p>
                    </div>

                    {/* CTA */}
                    <div className="mt-10 text-center">
                        <Link
                        href="/sg/personal-trainer"
                        prefetch={false}
                        className="inline-block px-8 py-3 text-white font-semibold rounded-full bg-linear-to-tr from-orange-600 to-amber-400 hover:opacity-90 transition"
                        >
                        Find Your Functional Fitness Trainer
                        </Link>
                    </div>
                </section>
                <section className="max-w-5xl mx-auto py-10 px-4">
                    <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">Expert Tips for Functional Fitness Mastery</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div
                            className="bg-linear-to-r from-blue-50 to-cyan-50 p-6 rounded-2xl border-l-4 border-blue-500"
                        >
                            <h3 className="text-lg font-semibold text-blue-800 mb-2">Movement Quality Over Quantity</h3>
                            <p className="text-gray-700 leading-6">Focus on perfect form in every rep. Slow, controlled movements build real strength and prevent injuries.</p>
                        </div>
                        <div
                            className="bg-linear-to-r from-green-50 to-emerald-50 p-6 rounded-2xl border-l-4 border-green-500"
                        >
                            <h3 className="text-lg font-semibold text-green-800 mb-2">Incorporate Compound Movements</h3>
                            <p className="text-gray-700 leading-6">Use full-body exercises like squats, deadlifts, and push-ups that work multiple muscle groups simultaneously.</p>
                        </div>
                        <div
                            className="bg-linear-to-r from-purple-50 to-violet-50 p-6 rounded-2xl border-l-4 border-purple-500"
                        >
                            <h3 className="text-lg font-semibold text-purple-800 mb-2">Progress with Variation</h3>
                            <p className="text-gray-700 leading-6">Change exercises regularly to challenge your body in new ways and prevent plateaus.</p>
                        </div>
                        <div
                            className="bg-linear-to-r from-orange-50 to-amber-50 p-6 rounded-2xl border-l-4 border-orange-500"
                        >
                            <h3 className="text-lg font-semibold text-orange-800 mb-2">Mind-Body Connection</h3>
                            <p className="text-gray-700 leading-6">Listen to your body cues. Functional fitness is about harmony between movement, breath, and awareness.</p>
                        </div>
                    </div>
                </section>                
                <PersonalTrainerWhyUs/>
                <PersonalTrainerBooking setIsQuickBook={setIsQuickBook} openRequestForm={openRequestForm} year={year} />
                <TrainerCustomerReviews testimonials={testimonials}/>
                <section className="relative py-8 md:py-12 overflow-hidden bg-slate-900">
                    <div className="absolute top-0 left-0 w-72 h-72 bg-orange-500/8 rounded-full blur-3xl" />
                    <div className='relative z-10 max-w-5xl mx-auto px-4'>
                        <DarkFaq 
                            title="Personal Training FAQs"
                            questionsAnswers={ptSingaporeFaqs}
                            // Same Q&A as the hub: only /sg/personal-trainer emits its FAQPage.
                            jsonLd={false}
                        />
                    </div>
                </section>
                <BlogsList blogs={fitnessBlogs} wpPosts={wpPosts} wpNewPosts={wpNewPosts} theme="dark"/>            
            </RequestProvider>
        </div>
        <Script
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());

            gtag('config', 'G-QD1HGGHDYJ');
          }}
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}
